// Device + install-context detection for the install / update flows.
export function getDeviceType() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = (navigator.userAgent || navigator.vendor || "").toLowerCase();
  // iPadOS 13+ reports as "MacIntel" with touch — treat as iOS.
  const isIPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  if (/iphone|ipad|ipod/.test(ua) || isIPadOS) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

// True when running inside the Median (GoNative) native WebView wrapper.
export function hasNativeBridge() {
  if (typeof window === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  return Boolean(window.median || window.gonative) || /median|gonative/.test(ua);
}

// Standalone display-mode (installed PWA) — informational only.
export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
}

// Full install context. Only Android (native or PWA/Chrome) should ever see update prompts.
export function getInstallContext() {
  const device = getDeviceType();
  const isAndroid = device === "android";
  const native = hasNativeBridge();
  return {
    device,
    isAndroid,
    isIOS: device === "ios",
    isDesktop: device === "desktop",
    isNativeApp: native,
    isNativeAndroid: isAndroid && native,
    isAndroidPWA: isAndroid && !native,
  };
}

export const isIOSDevice = () => getDeviceType() === "ios";
export const isAndroidDevice = () => getDeviceType() === "android";

// Three-way (four-state) push context per the web-push spec:
//  - "native-app"                → inside the Median shell; web push logic must NOT run
//  - "ios-pwa-not-installed"     → iOS Safari tab; must install to Home Screen first
//  - "ios-pwa-installed"         → iOS standalone PWA; can subscribe on user action
//  - "android-or-other-browser"  → regular Chrome/Firefox/Edge (Android or desktop); subscribe directly
export function getPushContext() {
  if (typeof window === "undefined") return "android-or-other-browser";
  if (hasNativeBridge()) return "native-app";
  const os = getDeviceType();
  const standalone = isStandalonePWA();
  if (os === "ios") return standalone ? "ios-pwa-installed" : "ios-pwa-not-installed";
  return "android-or-other-browser";
}

